import { cloneVNode, h, defineComponent, ref, onMounted, onUnmounted, provide, computed, inject, nextTick, toRaw, watchEffect } from 'vue';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function match(value, lookup) {
  if (value in lookup) {
    var returnValue = lookup[value];

    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }

    return typeof returnValue === 'function' ? returnValue.apply(void 0, args) : returnValue;
  }

  var error = new Error("Tried to handle \"" + value + "\" but there is no handler defined. Only defined handlers are: " + Object.keys(lookup).map(function (key) {
    return "\"" + key + "\"";
  }).join(', ') + ".");

  if (Error.captureStackTrace) {
    Error.captureStackTrace(error, match);
  }

  throw error;
}

function render(_ref) {
  var _slots$default;

  var props = _ref.props,
      attrs = _ref.attrs,
      slots = _ref.slots,
      slot = _ref.slot;

  var as = props.as,
      passThroughProps = _objectWithoutPropertiesLoose(props, ["as"]);

  var children = (_slots$default = slots["default"]) === null || _slots$default === void 0 ? void 0 : _slots$default.call(slots, slot);

  if (as === 'template') {
    if (Object.keys(passThroughProps).length > 0 || 'class' in attrs) {
      var _ref2 = children !== null && children !== void 0 ? children : [],
          firstChild = _ref2[0],
          other = _ref2.slice(1);

      if (other.length > 0) throw new Error('You should only render 1 child or use the `as="..."` prop');
      return cloneVNode(firstChild, passThroughProps);
    }

    return children;
  }

  return h(as, passThroughProps, children);
}

var id = 0;

function generateId() {
  return ++id;
}

function useId() {
  return generateId();
}

// TODO: This must already exist somewhere, right? 🤔
// Ref: https://www.w3.org/TR/uievents-key/#named-key-attribute-values
var Keys;

(function (Keys) {
  Keys["Space"] = " ";
  Keys["Enter"] = "Enter";
  Keys["Escape"] = "Escape";
  Keys["Backspace"] = "Backspace";
  Keys["ArrowUp"] = "ArrowUp";
  Keys["ArrowDown"] = "ArrowDown";
  Keys["Home"] = "Home";
  Keys["End"] = "End";
  Keys["PageUp"] = "PageUp";
  Keys["PageDown"] = "PageDown";
  Keys["Tab"] = "Tab";
})(Keys || (Keys = {}));

var MenuStates;

(function (MenuStates) {
  MenuStates[MenuStates["Open"] = 0] = "Open";
  MenuStates[MenuStates["Closed"] = 1] = "Closed";
})(MenuStates || (MenuStates = {}));

var Focus;

(function (Focus) {
  Focus[Focus["FirstItem"] = 0] = "FirstItem";
  Focus[Focus["PreviousItem"] = 1] = "PreviousItem";
  Focus[Focus["NextItem"] = 2] = "NextItem";
  Focus[Focus["LastItem"] = 3] = "LastItem";
  Focus[Focus["SpecificItem"] = 4] = "SpecificItem";
  Focus[Focus["Nothing"] = 5] = "Nothing";
})(Focus || (Focus = {}));

var MenuContext = /*#__PURE__*/Symbol('MenuContext');

function useMenuContext(component) {
  var context = inject(MenuContext);

  if (context === undefined) {
    var err = new Error("<" + component + " /> is missing a parent <Menu /> component.");
    if (Error.captureStackTrace) Error.captureStackTrace(err, useMenuContext);
    throw err;
  }

  return context;
}

var Menu = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    }
  },
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
        attrs = _ref.attrs;
    var menuState = ref(MenuStates.Closed);
    var buttonRef = ref(null);
    var itemsRef = ref(null);
    var items = ref([]);
    var searchQuery = ref('');
    var activeItemIndex = ref(null);

    function calculateActiveItemIndex(focus, id) {
      var _activeItemIndex$valu, _match;

      if (items.value.length <= 0) return null;
      var currentActiveItemIndex = (_activeItemIndex$valu = activeItemIndex.value) !== null && _activeItemIndex$valu !== void 0 ? _activeItemIndex$valu : -1;
      var nextActiveIndex = match(focus, (_match = {}, _match[Focus.FirstItem] = function () {
        return items.value.findIndex(function (item) {
          return !item.dataRef.disabled;
        });
      }, _match[Focus.PreviousItem] = function () {
        var idx = items.value.slice().reverse().findIndex(function (item, idx, all) {
          if (currentActiveItemIndex !== -1 && all.length - idx - 1 >= currentActiveItemIndex) return false;
          return !item.dataRef.disabled;
        });
        if (idx === -1) return idx;
        return items.value.length - 1 - idx;
      }, _match[Focus.NextItem] = function () {
        return items.value.findIndex(function (item, idx) {
          if (idx <= currentActiveItemIndex) return false;
          return !item.dataRef.disabled;
        });
      }, _match[Focus.LastItem] = function () {
        var idx = items.value.slice().reverse().findIndex(function (item) {
          return !item.dataRef.disabled;
        });
        if (idx === -1) return idx;
        return items.value.length - 1 - idx;
      }, _match[Focus.SpecificItem] = function () {
        return items.value.findIndex(function (item) {
          return item.id === id;
        });
      }, _match[Focus.Nothing] = function () {
        return null;
      }, _match));
      if (nextActiveIndex === -1) return activeItemIndex.value;
      return nextActiveIndex;
    }

    var api = {
      menuState: menuState,
      buttonRef: buttonRef,
      itemsRef: itemsRef,
      items: items,
      searchQuery: searchQuery,
      activeItemIndex: activeItemIndex,
      closeMenu: function closeMenu() {
        return menuState.value = MenuStates.Closed;
      },
      openMenu: function openMenu() {
        return menuState.value = MenuStates.Open;
      },
      goToItem: function goToItem(focus, id) {
        var nextActiveItemIndex = calculateActiveItemIndex(focus, id);
        if (searchQuery.value === '' && activeItemIndex.value === nextActiveItemIndex) return;
        searchQuery.value = '';
        activeItemIndex.value = nextActiveItemIndex;
      },
      search: function search(value) {
        searchQuery.value += value;
        var match = items.value.findIndex(function (item) {
          return item.dataRef.textValue.startsWith(searchQuery.value) && !item.dataRef.disabled;
        });

        if (match === -1 || match === activeItemIndex.value) {
          return;
        }

        activeItemIndex.value = match;
      },
      clearSearch: function clearSearch() {
        searchQuery.value = '';
      },
      registerItem: function registerItem(id, dataRef) {
        // @ts-expect-error The expected type comes from property 'dataRef' which is declared here on type '{ id: string; dataRef: { textValue: string; disabled: boolean; }; }'
        items.value.push({
          id: id,
          dataRef: dataRef
        });
      },
      unregisterItem: function unregisterItem(id) {
        var nextItems = items.value.slice();
        var currentActiveItem = activeItemIndex.value !== null ? nextItems[activeItemIndex.value] : null;
        var idx = nextItems.findIndex(function (a) {
          return a.id === id;
        });
        if (idx !== -1) nextItems.splice(idx, 1);
        items.value = nextItems;

        activeItemIndex.value = function () {
          if (idx === activeItemIndex.value) return null;
          if (currentActiveItem === null) return null; // If we removed the item before the actual active index, then it would be out of sync. To
          // fix this, we will find the correct (new) index position.

          return nextItems.indexOf(currentActiveItem);
        }();
      }
    };
    onMounted(function () {
      function handler(event) {
        var _buttonRef$value, _itemsRef$value;

        if (menuState.value !== MenuStates.Open) return;
        if ((_buttonRef$value = buttonRef.value) === null || _buttonRef$value === void 0 ? void 0 : _buttonRef$value.contains(event.target)) return;

        if (!((_itemsRef$value = itemsRef.value) === null || _itemsRef$value === void 0 ? void 0 : _itemsRef$value.contains(event.target))) {
          api.closeMenu();
        }

        if (!event.defaultPrevented) nextTick(function () {
          var _buttonRef$value2;

          return (_buttonRef$value2 = buttonRef.value) === null || _buttonRef$value2 === void 0 ? void 0 : _buttonRef$value2.focus();
        });
      }

      window.addEventListener('click', handler);
      onUnmounted(function () {
        return window.removeEventListener('click', handler);
      });
    }); // @ts-expect-error Types of property 'dataRef' are incompatible.

    provide(MenuContext, api);
    return function () {
      var slot = {
        open: menuState.value === MenuStates.Open
      };
      return render({
        props: props,
        slot: slot,
        slots: slots,
        attrs: attrs
      });
    };
  }
});
var MenuButton = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'button'
    }
  },
  render: function render$1() {
    var _api$itemsRef$value;

    var api = useMenuContext('MenuButton');
    var slot = {
      open: api.menuState.value === MenuStates.Open
    };
    var propsWeControl = {
      ref: 'el',
      id: this.id,
      type: 'button',
      'aria-haspopup': true,
      'aria-controls': (_api$itemsRef$value = api.itemsRef.value) === null || _api$itemsRef$value === void 0 ? void 0 : _api$itemsRef$value.id,
      'aria-expanded': api.menuState.value === MenuStates.Open ? true : undefined,
      onKeyDown: this.handleKeyDown,
      onFocus: this.handleFocus,
      onPointerUp: this.handlePointerUp
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useMenuContext('MenuButton');
    var id = "headlessui-menu-button-" + useId();

    function handleKeyDown(event) {
      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-13
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault();
          api.openMenu();
          nextTick(function () {
            var _api$itemsRef$value2;

            (_api$itemsRef$value2 = api.itemsRef.value) === null || _api$itemsRef$value2 === void 0 ? void 0 : _api$itemsRef$value2.focus();
            api.goToItem(Focus.FirstItem);
          });
          break;

        case Keys.ArrowUp:
          event.preventDefault();
          api.openMenu();
          nextTick(function () {
            var _api$itemsRef$value3;

            (_api$itemsRef$value3 = api.itemsRef.value) === null || _api$itemsRef$value3 === void 0 ? void 0 : _api$itemsRef$value3.focus();
            api.goToItem(Focus.LastItem);
          });
          break;
      }
    }

    function handlePointerUp(event) {
      if (api.menuState.value === MenuStates.Open) {
        api.closeMenu();
        nextTick(function () {
          var _api$buttonRef$value;

          return (_api$buttonRef$value = api.buttonRef.value) === null || _api$buttonRef$value === void 0 ? void 0 : _api$buttonRef$value.focus();
        });
      } else {
        event.preventDefault();
        api.openMenu();
        nextTick(function () {
          var _api$itemsRef$value4;

          return (_api$itemsRef$value4 = api.itemsRef.value) === null || _api$itemsRef$value4 === void 0 ? void 0 : _api$itemsRef$value4.focus();
        });
      }
    }

    function handleFocus() {
      var _api$itemsRef$value5;

      if (api.menuState.value === MenuStates.Open) (_api$itemsRef$value5 = api.itemsRef.value) === null || _api$itemsRef$value5 === void 0 ? void 0 : _api$itemsRef$value5.focus();
    }

    return {
      id: id,
      el: api.buttonRef,
      handleKeyDown: handleKeyDown,
      handlePointerUp: handlePointerUp,
      handleFocus: handleFocus
    };
  }
});
var MenuItems = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'div'
    },
    "static": {
      type: Boolean,
      "default": false
    }
  },
  render: function render$1() {
    var _api$items$value$api$, _api$buttonRef$value2;

    var api = useMenuContext('MenuItems'); // `static` is a reserved keyword, therefore aliasing it...

    var _this$$props = this.$props,
        isStatic = _this$$props["static"],
        passThroughProps = _objectWithoutPropertiesLoose(_this$$props, ["static"]);

    if (!isStatic && api.menuState.value === MenuStates.Closed) return null;
    var slot = {
      open: api.menuState.value === MenuStates.Open
    };
    var propsWeControl = {
      'aria-activedescendant': api.activeItemIndex.value === null ? undefined : (_api$items$value$api$ = api.items.value[api.activeItemIndex.value]) === null || _api$items$value$api$ === void 0 ? void 0 : _api$items$value$api$.id,
      'aria-labelledby': (_api$buttonRef$value2 = api.buttonRef.value) === null || _api$buttonRef$value2 === void 0 ? void 0 : _api$buttonRef$value2.id,
      id: this.id,
      onKeyDown: this.handleKeyDown,
      role: 'menu',
      tabIndex: 0,
      ref: 'el'
    };
    return render({
      props: _extends({}, passThroughProps, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useMenuContext('MenuItems');
    var id = "headlessui-menu-items-" + useId();
    var searchDebounce = ref(null);

    function handleKeyDown(event) {
      if (searchDebounce.value) clearTimeout(searchDebounce.value);

      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-12
        // @ts-expect-error Fallthrough is expected here
        case Keys.Space:
          if (api.searchQuery.value !== '') {
            event.preventDefault();
            return api.search(event.key);
          }

        // When in type ahead mode, fallthrough

        case Keys.Enter:
          event.preventDefault();
          api.closeMenu();

          if (api.activeItemIndex.value !== null) {
            var _document$getElementB;

            var _id = api.items.value[api.activeItemIndex.value].id;
            (_document$getElementB = document.getElementById(_id)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.click();
          }

          nextTick(function () {
            var _api$buttonRef$value3;

            return (_api$buttonRef$value3 = api.buttonRef.value) === null || _api$buttonRef$value3 === void 0 ? void 0 : _api$buttonRef$value3.focus();
          });
          break;

        case Keys.ArrowDown:
          event.preventDefault();
          return api.goToItem(Focus.NextItem);

        case Keys.ArrowUp:
          event.preventDefault();
          return api.goToItem(Focus.PreviousItem);

        case Keys.Home:
        case Keys.PageUp:
          event.preventDefault();
          return api.goToItem(Focus.FirstItem);

        case Keys.End:
        case Keys.PageDown:
          event.preventDefault();
          return api.goToItem(Focus.LastItem);

        case Keys.Escape:
          event.preventDefault();
          api.closeMenu();
          nextTick(function () {
            var _api$buttonRef$value4;

            return (_api$buttonRef$value4 = api.buttonRef.value) === null || _api$buttonRef$value4 === void 0 ? void 0 : _api$buttonRef$value4.focus();
          });
          break;

        case Keys.Tab:
          return event.preventDefault();

        default:
          if (event.key.length === 1) {
            api.search(event.key);
            searchDebounce.value = setTimeout(function () {
              return api.clearSearch();
            }, 350);
          }

          break;
      }
    }

    return {
      id: id,
      el: api.itemsRef,
      handleKeyDown: handleKeyDown
    };
  }
});
var MenuItem = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    },
    disabled: {
      type: Boolean,
      "default": false
    },
    "class": {
      type: [String, Function],
      required: false
    },
    className: {
      type: [String, Function],
      required: false
    }
  },
  setup: function setup(props, _ref2) {
    var slots = _ref2.slots,
        attrs = _ref2.attrs;
    var api = useMenuContext('MenuItem');
    var id = "headlessui-menu-item-" + useId();
    var disabled = props.disabled,
        defaultClass = props["class"],
        _props$className = props.className,
        className = _props$className === void 0 ? defaultClass : _props$className;
    var active = computed(function () {
      return api.activeItemIndex.value !== null ? api.items.value[api.activeItemIndex.value].id === id : false;
    });
    var dataRef = ref({
      disabled: disabled,
      textValue: ''
    });
    onMounted(function () {
      var _document$getElementB2, _document$getElementB3;

      var textValue = (_document$getElementB2 = document.getElementById(id)) === null || _document$getElementB2 === void 0 ? void 0 : (_document$getElementB3 = _document$getElementB2.textContent) === null || _document$getElementB3 === void 0 ? void 0 : _document$getElementB3.toLowerCase().trim();
      if (textValue !== undefined) dataRef.value.textValue = textValue;
    });
    onMounted(function () {
      return api.registerItem(id, dataRef);
    });
    onUnmounted(function () {
      return api.unregisterItem(id);
    });

    function handleClick(event) {
      if (disabled) return event.preventDefault();
      api.closeMenu();
      nextTick(function () {
        var _api$buttonRef$value5;

        return (_api$buttonRef$value5 = api.buttonRef.value) === null || _api$buttonRef$value5 === void 0 ? void 0 : _api$buttonRef$value5.focus();
      });
    }

    function handleFocus() {
      if (disabled) return api.goToItem(Focus.Nothing);
      api.goToItem(Focus.SpecificItem, id);
    }

    function handlePointerMove() {
      if (disabled) return;
      if (active.value) return;
      api.goToItem(Focus.SpecificItem, id);
    }

    function handlePointerLeave() {
      if (disabled) return;
      if (!active.value) return;
      api.goToItem(Focus.Nothing);
    }

    return function () {
      var slot = {
        active: active.value,
        disabled: disabled
      };
      var propsWeControl = {
        id: id,
        role: 'menuitem',
        tabIndex: -1,
        "class": resolvePropValue(className, slot),
        'aria-disabled': disabled === true ? true : undefined,
        onClick: handleClick,
        onFocus: handleFocus,
        onPointerMove: handlePointerMove,
        onPointerLeave: handlePointerLeave
      };
      return render({
        props: _extends({}, props, propsWeControl),
        slot: slot,
        attrs: attrs,
        slots: slots
      });
    };
  }
});

function resolvePropValue(property, bag) {
  if (property === undefined) return undefined;
  if (typeof property === 'function') return property(bag);
  return property;
}

var ListboxStates;

(function (ListboxStates) {
  ListboxStates[ListboxStates["Open"] = 0] = "Open";
  ListboxStates[ListboxStates["Closed"] = 1] = "Closed";
})(ListboxStates || (ListboxStates = {}));

var Focus$1;

(function (Focus) {
  Focus[Focus["First"] = 0] = "First";
  Focus[Focus["Previous"] = 1] = "Previous";
  Focus[Focus["Next"] = 2] = "Next";
  Focus[Focus["Last"] = 3] = "Last";
  Focus[Focus["Specific"] = 4] = "Specific";
  Focus[Focus["Nothing"] = 5] = "Nothing";
})(Focus$1 || (Focus$1 = {}));

var ListboxContext = /*#__PURE__*/Symbol('ListboxContext');

function useListboxContext(component) {
  var context = inject(ListboxContext);

  if (context === undefined) {
    var err = new Error("<" + component + " /> is missing a parent <Listbox /> component.");
    if (Error.captureStackTrace) Error.captureStackTrace(err, useListboxContext);
    throw err;
  }

  return context;
} // ---


var Listbox = /*#__PURE__*/defineComponent({
  name: 'Listbox',
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    },
    modelValue: {
      type: [Object, String],
      "default": null
    }
  },
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
        attrs = _ref.attrs,
        emit = _ref.emit;

    var passThroughProps = _objectWithoutPropertiesLoose(props, ["modelValue"]);

    var listboxState = ref(ListboxStates.Closed);
    var labelRef = ref(null);
    var buttonRef = ref(null);
    var optionsRef = ref(null);
    var options = ref([]);
    var searchQuery = ref('');
    var activeOptionIndex = ref(null);
    var value = computed(function () {
      return props.modelValue;
    });

    function calculateActiveOptionIndex(focus, id) {
      var _activeOptionIndex$va, _match;

      if (options.value.length <= 0) return null;
      var currentActiveOptionIndex = (_activeOptionIndex$va = activeOptionIndex.value) !== null && _activeOptionIndex$va !== void 0 ? _activeOptionIndex$va : -1;
      var nextActiveIndex = match(focus, (_match = {}, _match[Focus$1.First] = function () {
        return options.value.findIndex(function (option) {
          return !option.dataRef.disabled;
        });
      }, _match[Focus$1.Previous] = function () {
        var idx = options.value.slice().reverse().findIndex(function (option, idx, all) {
          if (currentActiveOptionIndex !== -1 && all.length - idx - 1 >= currentActiveOptionIndex) return false;
          return !option.dataRef.disabled;
        });
        if (idx === -1) return idx;
        return options.value.length - 1 - idx;
      }, _match[Focus$1.Next] = function () {
        return options.value.findIndex(function (option, idx) {
          if (idx <= currentActiveOptionIndex) return false;
          return !option.dataRef.disabled;
        });
      }, _match[Focus$1.Last] = function () {
        var idx = options.value.slice().reverse().findIndex(function (option) {
          return !option.dataRef.disabled;
        });
        if (idx === -1) return idx;
        return options.value.length - 1 - idx;
      }, _match[Focus$1.Specific] = function () {
        return options.value.findIndex(function (option) {
          return option.id === id;
        });
      }, _match[Focus$1.Nothing] = function () {
        return null;
      }, _match));
      if (nextActiveIndex === -1) return activeOptionIndex.value;
      return nextActiveIndex;
    }

    var api = {
      listboxState: listboxState,
      value: value,
      labelRef: labelRef,
      buttonRef: buttonRef,
      optionsRef: optionsRef,
      options: options,
      searchQuery: searchQuery,
      activeOptionIndex: activeOptionIndex,
      closeListbox: function closeListbox() {
        return listboxState.value = ListboxStates.Closed;
      },
      openListbox: function openListbox() {
        return listboxState.value = ListboxStates.Open;
      },
      goToOption: function goToOption(focus, id) {
        var nextActiveOptionIndex = calculateActiveOptionIndex(focus, id);
        if (searchQuery.value === '' && activeOptionIndex.value === nextActiveOptionIndex) return;
        searchQuery.value = '';
        activeOptionIndex.value = nextActiveOptionIndex;
      },
      search: function search(value) {
        searchQuery.value += value;
        var match = options.value.findIndex(function (option) {
          return !option.dataRef.disabled && option.dataRef.textValue.startsWith(searchQuery.value);
        });

        if (match === -1 || match === activeOptionIndex.value) {
          return;
        }

        activeOptionIndex.value = match;
      },
      clearSearch: function clearSearch() {
        searchQuery.value = '';
      },
      registerOption: function registerOption(id, dataRef) {
        // @ts-expect-error The expected type comes from property 'dataRef' which is declared here on type '{ id: string; dataRef: { textValue: string; disabled: boolean; }; }'
        options.value.push({
          id: id,
          dataRef: dataRef
        });
      },
      unregisterOption: function unregisterOption(id) {
        var nextOptions = options.value.slice();
        var currentActiveOption = activeOptionIndex.value !== null ? nextOptions[activeOptionIndex.value] : null;
        var idx = nextOptions.findIndex(function (a) {
          return a.id === id;
        });
        if (idx !== -1) nextOptions.splice(idx, 1);
        options.value = nextOptions;

        activeOptionIndex.value = function () {
          if (idx === activeOptionIndex.value) return null;
          if (currentActiveOption === null) return null; // If we removed the option before the actual active index, then it would be out of sync. To
          // fix this, we will find the correct (new) index position.

          return nextOptions.indexOf(currentActiveOption);
        }();
      },
      select: function select(value) {
        emit('update:modelValue', value);
      }
    };
    onMounted(function () {
      function handler(event) {
        var _buttonRef$value, _optionsRef$value;

        if (listboxState.value !== ListboxStates.Open) return;
        if ((_buttonRef$value = buttonRef.value) === null || _buttonRef$value === void 0 ? void 0 : _buttonRef$value.contains(event.target)) return;

        if (!((_optionsRef$value = optionsRef.value) === null || _optionsRef$value === void 0 ? void 0 : _optionsRef$value.contains(event.target))) {
          api.closeListbox();
        }

        if (!event.defaultPrevented) nextTick(function () {
          var _buttonRef$value2;

          return (_buttonRef$value2 = buttonRef.value) === null || _buttonRef$value2 === void 0 ? void 0 : _buttonRef$value2.focus();
        });
      }

      window.addEventListener('click', handler);
      onUnmounted(function () {
        return window.removeEventListener('click', handler);
      });
    }); // @ts-expect-error Types of property 'dataRef' are incompatible.

    provide(ListboxContext, api);
    return function () {
      var slot = {
        open: listboxState.value === ListboxStates.Open
      };
      return render({
        props: passThroughProps,
        slot: slot,
        slots: slots,
        attrs: attrs
      });
    };
  }
}); // ---

var ListboxLabel = /*#__PURE__*/defineComponent({
  name: 'ListboxLabel',
  props: {
    as: {
      type: [Object, String],
      "default": 'label'
    }
  },
  render: function render$1() {
    var api = useListboxContext('ListboxLabel');
    var slot = {
      open: api.listboxState.value === ListboxStates.Open
    };
    var propsWeControl = {
      id: this.id,
      ref: 'el',
      onPointerUp: this.handlePointerUp
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useListboxContext('ListboxLabel');
    var id = "headlessui-listbox-label-" + useId();
    return {
      id: id,
      el: api.labelRef,
      handlePointerUp: function handlePointerUp() {
        var _api$buttonRef$value;

        (_api$buttonRef$value = api.buttonRef.value) === null || _api$buttonRef$value === void 0 ? void 0 : _api$buttonRef$value.focus();
      }
    };
  }
}); // ---

var ListboxButton = /*#__PURE__*/defineComponent({
  name: 'ListboxButton',
  props: {
    as: {
      type: [Object, String],
      "default": 'button'
    }
  },
  render: function render$1() {
    var _api$optionsRef$value;

    var api = useListboxContext('ListboxButton');
    var slot = {
      open: api.listboxState.value === ListboxStates.Open,
      focused: this.focused
    };
    var propsWeControl = {
      ref: 'el',
      id: this.id,
      type: 'button',
      'aria-haspopup': true,
      'aria-controls': (_api$optionsRef$value = api.optionsRef.value) === null || _api$optionsRef$value === void 0 ? void 0 : _api$optionsRef$value.id,
      'aria-expanded': api.listboxState.value === ListboxStates.Open ? true : undefined,
      'aria-labelledby': api.labelRef.value ? [api.labelRef.value.id, this.id].join(' ') : undefined,
      onKeyDown: this.handleKeyDown,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onPointerUp: this.handlePointerUp
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useListboxContext('ListboxButton');
    var id = "headlessui-listbox-button-" + useId();
    var focused = ref(false);

    function handleKeyDown(event) {
      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-13
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault();
          api.openListbox();
          nextTick(function () {
            var _api$optionsRef$value2;

            (_api$optionsRef$value2 = api.optionsRef.value) === null || _api$optionsRef$value2 === void 0 ? void 0 : _api$optionsRef$value2.focus();
            if (!api.value.value) api.goToOption(Focus$1.First);
          });
          break;

        case Keys.ArrowUp:
          event.preventDefault();
          api.openListbox();
          nextTick(function () {
            var _api$optionsRef$value3;

            (_api$optionsRef$value3 = api.optionsRef.value) === null || _api$optionsRef$value3 === void 0 ? void 0 : _api$optionsRef$value3.focus();
            if (!api.value.value) api.goToOption(Focus$1.Last);
          });
          break;
      }
    }

    function handlePointerUp(event) {
      if (api.listboxState.value === ListboxStates.Open) {
        api.closeListbox();
        nextTick(function () {
          var _api$buttonRef$value2;

          return (_api$buttonRef$value2 = api.buttonRef.value) === null || _api$buttonRef$value2 === void 0 ? void 0 : _api$buttonRef$value2.focus();
        });
      } else {
        event.preventDefault();
        api.openListbox();
        nextTick(function () {
          var _api$optionsRef$value4;

          return (_api$optionsRef$value4 = api.optionsRef.value) === null || _api$optionsRef$value4 === void 0 ? void 0 : _api$optionsRef$value4.focus();
        });
      }
    }

    function handleFocus() {
      var _api$optionsRef$value5;

      if (api.listboxState.value === ListboxStates.Open) return (_api$optionsRef$value5 = api.optionsRef.value) === null || _api$optionsRef$value5 === void 0 ? void 0 : _api$optionsRef$value5.focus();
      focused.value = true;
    }

    function handleBlur() {
      focused.value = false;
    }

    return {
      id: id,
      el: api.buttonRef,
      focused: focused,
      handleKeyDown: handleKeyDown,
      handlePointerUp: handlePointerUp,
      handleFocus: handleFocus,
      handleBlur: handleBlur
    };
  }
}); // ---

var ListboxOptions = /*#__PURE__*/defineComponent({
  name: 'ListboxOptions',
  props: {
    as: {
      type: [Object, String],
      "default": 'ul'
    },
    "static": {
      type: Boolean,
      "default": false
    }
  },
  render: function render$1() {
    var _api$options$value$ap, _api$labelRef$value$i, _api$labelRef$value, _api$buttonRef$value3;

    var api = useListboxContext('ListboxOptions'); // `static` is a reserved keyword, therefore aliasing it...

    var _this$$props = this.$props,
        isStatic = _this$$props["static"],
        passThroughProps = _objectWithoutPropertiesLoose(_this$$props, ["static"]);

    if (!isStatic && api.listboxState.value === ListboxStates.Closed) return null;
    var slot = {
      open: api.listboxState.value === ListboxStates.Open
    };
    var propsWeControl = {
      'aria-activedescendant': api.activeOptionIndex.value === null ? undefined : (_api$options$value$ap = api.options.value[api.activeOptionIndex.value]) === null || _api$options$value$ap === void 0 ? void 0 : _api$options$value$ap.id,
      'aria-labelledby': (_api$labelRef$value$i = (_api$labelRef$value = api.labelRef.value) === null || _api$labelRef$value === void 0 ? void 0 : _api$labelRef$value.id) !== null && _api$labelRef$value$i !== void 0 ? _api$labelRef$value$i : (_api$buttonRef$value3 = api.buttonRef.value) === null || _api$buttonRef$value3 === void 0 ? void 0 : _api$buttonRef$value3.id,
      id: this.id,
      onKeyDown: this.handleKeyDown,
      role: 'listbox',
      tabIndex: 0,
      ref: 'el'
    };
    return render({
      props: _extends({}, passThroughProps, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useListboxContext('ListboxOptions');
    var id = "headlessui-listbox-options-" + useId();
    var searchDebounce = ref(null);

    function handleKeyDown(event) {
      if (searchDebounce.value) clearTimeout(searchDebounce.value);

      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-12
        // @ts-expect-error Fallthrough is expected here
        case Keys.Space:
          if (api.searchQuery.value !== '') {
            event.preventDefault();
            return api.search(event.key);
          }

        // When in type ahead mode, fallthrough

        case Keys.Enter:
          event.preventDefault();
          api.closeListbox();

          if (api.activeOptionIndex.value !== null) {
            var dataRef = api.options.value[api.activeOptionIndex.value].dataRef;
            api.select(dataRef.value);
          }

          nextTick(function () {
            var _api$buttonRef$value4;

            return (_api$buttonRef$value4 = api.buttonRef.value) === null || _api$buttonRef$value4 === void 0 ? void 0 : _api$buttonRef$value4.focus();
          });
          break;

        case Keys.ArrowDown:
          event.preventDefault();
          return api.goToOption(Focus$1.Next);

        case Keys.ArrowUp:
          event.preventDefault();
          return api.goToOption(Focus$1.Previous);

        case Keys.Home:
        case Keys.PageUp:
          event.preventDefault();
          return api.goToOption(Focus$1.First);

        case Keys.End:
        case Keys.PageDown:
          event.preventDefault();
          return api.goToOption(Focus$1.Last);

        case Keys.Escape:
          event.preventDefault();
          api.closeListbox();
          nextTick(function () {
            var _api$buttonRef$value5;

            return (_api$buttonRef$value5 = api.buttonRef.value) === null || _api$buttonRef$value5 === void 0 ? void 0 : _api$buttonRef$value5.focus();
          });
          break;

        case Keys.Tab:
          return event.preventDefault();

        default:
          if (event.key.length === 1) {
            api.search(event.key);
            searchDebounce.value = setTimeout(function () {
              return api.clearSearch();
            }, 350);
          }

          break;
      }
    }

    return {
      id: id,
      el: api.optionsRef,
      handleKeyDown: handleKeyDown
    };
  }
});
var ListboxOption = /*#__PURE__*/defineComponent({
  name: 'ListboxOption',
  props: {
    as: {
      type: [Object, String],
      "default": 'li'
    },
    value: {
      type: [Object, String],
      "default": null
    },
    disabled: {
      type: Boolean,
      "default": false
    },
    "class": {
      type: [String, Function],
      required: false
    },
    className: {
      type: [String, Function],
      required: false
    }
  },
  setup: function setup(props, _ref2) {
    var slots = _ref2.slots,
        attrs = _ref2.attrs;
    var api = useListboxContext('ListboxOption');
    var id = "headlessui-listbox-option-" + useId();
    var disabled = props.disabled,
        defaultClass = props["class"],
        _props$className = props.className,
        className = _props$className === void 0 ? defaultClass : _props$className,
        value = props.value;
    var active = computed(function () {
      return api.activeOptionIndex.value !== null ? api.options.value[api.activeOptionIndex.value].id === id : false;
    });
    var selected = computed(function () {
      return toRaw(api.value.value) === toRaw(value);
    });
    var dataRef = ref({
      disabled: disabled,
      value: value,
      textValue: ''
    });
    onMounted(function () {
      var _document$getElementB, _document$getElementB2;

      var textValue = (_document$getElementB = document.getElementById(id)) === null || _document$getElementB === void 0 ? void 0 : (_document$getElementB2 = _document$getElementB.textContent) === null || _document$getElementB2 === void 0 ? void 0 : _document$getElementB2.toLowerCase().trim();
      if (textValue !== undefined) dataRef.value.textValue = textValue;
    });
    onMounted(function () {
      return api.registerOption(id, dataRef);
    });
    onUnmounted(function () {
      return api.unregisterOption(id);
    });
    onMounted(function () {
      var _document$getElementB3, _document$getElementB4;

      if (!selected.value) return;
      api.goToOption(Focus$1.Specific, id);
      (_document$getElementB3 = document.getElementById(id)) === null || _document$getElementB3 === void 0 ? void 0 : (_document$getElementB4 = _document$getElementB3.focus) === null || _document$getElementB4 === void 0 ? void 0 : _document$getElementB4.call(_document$getElementB3);
    });
    watchEffect(function () {
      if (!active.value) return;
      nextTick(function () {
        var _document$getElementB5, _document$getElementB6;

        return (_document$getElementB5 = document.getElementById(id)) === null || _document$getElementB5 === void 0 ? void 0 : (_document$getElementB6 = _document$getElementB5.scrollIntoView) === null || _document$getElementB6 === void 0 ? void 0 : _document$getElementB6.call(_document$getElementB5, {
          block: 'nearest'
        });
      });
    });

    function handleClick(event) {
      if (disabled) return event.preventDefault();
      api.select(value);
      api.closeListbox();
      nextTick(function () {
        var _api$buttonRef$value6;

        return (_api$buttonRef$value6 = api.buttonRef.value) === null || _api$buttonRef$value6 === void 0 ? void 0 : _api$buttonRef$value6.focus();
      });
    }

    function handleFocus() {
      if (disabled) return api.goToOption(Focus$1.Nothing);
      api.goToOption(Focus$1.Specific, id);
    }

    function handlePointerMove() {
      if (disabled) return;
      if (active.value) return;
      api.goToOption(Focus$1.Specific, id);
    }

    function handlePointerLeave() {
      if (disabled) return;
      if (!active.value) return;
      api.goToOption(Focus$1.Nothing);
    }

    return function () {
      var slot = {
        active: active.value,
        selected: selected.value,
        disabled: disabled
      };
      var propsWeControl = {
        id: id,
        role: 'option',
        tabIndex: -1,
        "class": resolvePropValue$1(className, slot),
        'aria-disabled': disabled === true ? true : undefined,
        'aria-selected': selected.value === true ? selected.value : undefined,
        onClick: handleClick,
        onFocus: handleFocus,
        onPointerMove: handlePointerMove,
        onPointerLeave: handlePointerLeave
      };
      return render({
        props: _extends({}, props, propsWeControl),
        slot: slot,
        attrs: attrs,
        slots: slots
      });
    };
  }
}); // ---

function resolvePropValue$1(property, bag) {
  if (property === undefined) return undefined;
  if (typeof property === 'function') return property(bag);
  return property;
}

var GroupContext = /*#__PURE__*/Symbol('GroupContext');

function useGroupContext(component) {
  var context = inject(GroupContext, null);

  if (context === null) {
    var err = new Error("<" + component + " /> is missing a parent <SwitchGroup /> component.");
    if (Error.captureStackTrace) Error.captureStackTrace(err, useGroupContext);
    throw err;
  }

  return context;
} // ---


var SwitchGroup = /*#__PURE__*/defineComponent({
  name: 'SwitchGroup',
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    }
  },
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
        attrs = _ref.attrs;
    var switchRef = ref(null);
    var labelRef = ref(null);
    var api = {
      switchRef: switchRef,
      labelRef: labelRef
    };
    provide(GroupContext, api);
    return function () {
      return render({
        props: props,
        slot: {},
        slots: slots,
        attrs: attrs
      });
    };
  }
}); // ---

var Switch = /*#__PURE__*/defineComponent({
  name: 'Switch',
  props: {
    as: {
      type: [Object, String],
      "default": 'button'
    },
    modelValue: {
      type: [Object, Boolean],
      "default": null
    },
    "class": {
      type: [String, Function],
      required: false
    },
    className: {
      type: [String, Function],
      required: false
    }
  },
  render: function render$1() {
    var api = inject(GroupContext, null);
    var _this$$props = this.$props,
        defaultClass = _this$$props["class"],
        _this$$props$classNam = _this$$props.className,
        className = _this$$props$classNam === void 0 ? defaultClass : _this$$props$classNam;
    var labelledby = computed(function () {
      var _api$labelRef$value;

      return api === null || api === void 0 ? void 0 : (_api$labelRef$value = api.labelRef.value) === null || _api$labelRef$value === void 0 ? void 0 : _api$labelRef$value.id;
    });
    var slot = {
      checked: this.$props.modelValue
    };
    var propsWeControl = {
      id: this.id,
      ref: api === null ? undefined : api.switchRef,
      role: 'switch',
      tabIndex: 0,
      "class": resolvePropValue$2(className, slot),
      'aria-checked': this.$props.modelValue,
      'aria-labelledby': labelledby.value,
      onClick: this.handleClick,
      onKeyUp: this.handleKeyUp
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup(props, _ref2) {
    var emit = _ref2.emit;
    var api = inject(GroupContext, null);
    var id = "headlessui-switch-" + useId();

    function toggle() {
      emit('update:modelValue', !props.modelValue);
    }

    return {
      id: id,
      el: api === null || api === void 0 ? void 0 : api.switchRef,
      handleClick: function handleClick(event) {
        event.preventDefault();
        toggle();
      },
      handleKeyUp: function handleKeyUp(event) {
        if (event.key === Keys.Space) {
          event.preventDefault();
          toggle();
        }
      }
    };
  }
}); // ---

var SwitchLabel = /*#__PURE__*/defineComponent({
  name: 'SwitchLabel',
  props: {
    as: {
      type: [Object, String],
      "default": 'label'
    }
  },
  render: function render$1() {
    var propsWeControl = {
      id: this.id,
      ref: 'el',
      onPointerUp: this.handlePointerUp
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: {},
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useGroupContext('SwitchLabel');
    var id = "headlessui-switch-label-" + useId();
    return {
      id: id,
      el: api.labelRef,
      handlePointerUp: function handlePointerUp() {
        var _api$switchRef$value, _api$switchRef$value2;

        (_api$switchRef$value = api.switchRef.value) === null || _api$switchRef$value === void 0 ? void 0 : _api$switchRef$value.click();
        (_api$switchRef$value2 = api.switchRef.value) === null || _api$switchRef$value2 === void 0 ? void 0 : _api$switchRef$value2.focus();
      }
    };
  }
}); // ---

function resolvePropValue$2(property, bag) {
  if (property === undefined) return undefined;
  if (typeof property === 'function') return property(bag);
  return property;
}

export { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems, Switch, SwitchGroup, SwitchLabel };
//# sourceMappingURL=headlessui.esm.js.map
